<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { required, minLength, sameAs, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { validatePasswordToken, setPasswordByToken } from '@/api/auth'
import FormInput from '@/components/Form/Input.vue'
import Button from '@/components/Button.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const token = computed(() => route.query.token || '')

// 'checking' | 'form' | 'invalid' | 'done'
const state = ref('checking')
const saving = ref(false)
const errorMessage = ref('')

const form = reactive({
  password: '',
  password_confirmation: '',
})

// Mirrors the server rule: at least 8 characters with letters and digits
const lettersAndDigits = helpers.regex(/^(?=.*\p{L})(?=.*\d).*$/u)

const rules = computed(() => ({
  password: { required, minLength: minLength(8), lettersAndDigits },
  password_confirmation: { required, sameAs: sameAs(form.password) },
}))

const v$ = useVuelidate(rules, form)

onMounted(async () => {
  try {
    await validatePasswordToken(token.value)
    state.value = 'form'
  } catch (error) {
    state.value = 'invalid'
  }
})

const submit = async () => {
  v$.value.$touch()
  errorMessage.value = ''
  if (v$.value.$invalid) {
    if (v$.value.password_confirmation.sameAs.$invalid) {
      errorMessage.value = t('SET_PASSWORD.MISMATCH')
    } else if (v$.value.password.lettersAndDigits.$invalid) {
      errorMessage.value = t('SET_PASSWORD.WEAK')
    } else {
      errorMessage.value = t('SET_PASSWORD.TOO_SHORT')
    }
    return
  }
  saving.value = true
  try {
    await setPasswordByToken({ token: token.value, ...form })
    state.value = 'done'
  } catch (error) {
    if (error?.response?.status === 404) {
      state.value = 'invalid'
    } else {
      errorMessage.value =
        error?.response?.data?.message || t('SET_PASSWORD.ERROR')
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <main
    class="flex flex-col w-full min-h-screen py-20 bg-n-brand/5 dark:bg-n-background sm:px-6 lg:px-8"
  >
    <section class="max-w-5xl mx-auto">
      <h2 class="mt-6 text-3xl font-medium text-center text-n-slate-12">
        {{ $t('SET_PASSWORD.TITLE') }}
      </h2>
    </section>

    <section
      class="bg-white shadow sm:mx-auto mt-11 sm:w-full sm:max-w-lg dark:bg-n-solid-2 p-11 sm:shadow-lg sm:rounded-lg"
    >
      <p v-if="state === 'checking'" class="text-sm text-n-slate-11">
        {{ $t('SET_PASSWORD.CHECKING') }}
      </p>

      <template v-else-if="state === 'invalid'">
        <p class="text-sm text-n-slate-12">
          {{ $t('SET_PASSWORD.INVALID') }}
        </p>
      </template>

      <template v-else-if="state === 'done'">
        <p class="mb-5 text-sm text-n-slate-12">
          {{ $t('SET_PASSWORD.DONE') }}
        </p>
        <Button
          lg
          justify="center"
          class="w-full"
          :label="$t('SET_PASSWORD.TO_LOGIN')"
          @click="router.push({ name: 'login' })"
        />
      </template>

      <form v-else class="space-y-5" @submit.prevent="submit">
        <div
          v-if="errorMessage"
          class="rounded-md bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
        >
          {{ errorMessage }}
        </div>
        <FormInput
          v-model="form.password"
          type="password"
          name="new_password"
          required
          :tabindex="1"
          :label="$t('SET_PASSWORD.PASSWORD.LABEL')"
          :placeholder="$t('SET_PASSWORD.PASSWORD.PLACEHOLDER')"
          :has-error="v$.password.$error"
          @input="v$.password.$touch"
        />
        <FormInput
          v-model="form.password_confirmation"
          type="password"
          name="confirm_password"
          required
          :tabindex="2"
          :label="$t('SET_PASSWORD.CONFIRM.LABEL')"
          :placeholder="$t('SET_PASSWORD.CONFIRM.PLACEHOLDER')"
          :has-error="v$.password_confirmation.$error"
          @input="v$.password_confirmation.$touch"
        />
        <Button
          lg
          justify="center"
          type="submit"
          class="w-full"
          :tabindex="3"
          :label="$t('SET_PASSWORD.SUBMIT')"
          :disabled="saving"
        />
      </form>
    </section>
  </main>
</template>
